import React, { useMemo, useState } from "react";
import { Alert, Button, Input, Tag } from "antd";
import useLang from "../../../hooks/useLang";
import useBulkOrderOperation from "../../../hooks/useBulkOrderOperation";
import { BulkOrderOperationType } from "../../../api/bulk-order-operations";
import {
	MAX_ORDER_IDS,
	getCategoryConfig,
} from "../../../utils/bulkOperationsConfig";
import { parseOrderIds } from "../parseOrderIds";
import BulkOpsHeader from "../components/BulkOpsHeader";
import ConfirmModal from "../components/ConfirmModal";
import OperationCards, { OperationConfig } from "../components/OperationCards";
import OrderIdsInput from "../components/OrderIdsInput";
import ResultPanels from "../components/ResultPanels";
import { PRIMARY_BUTTON_STYLE } from "../components/styles";
import { AMK_ORDERS_OPERATIONS } from "./operations";

const MAX_COMMENT_LENGTH = 500;

const AmkOrders = () => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;
	const copy = strings?.amkOrders;
	const categoryStrings = strings?.categories?.amkOrders;

	const categoryConfig = getCategoryConfig("amk-orders");

	const [operation, setOperation] = useState<BulkOrderOperationType | null>(
		null
	);
	const [rawInput, setRawInput] = useState("");
	const [comment, setComment] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmWord, setConfirmWord] = useState("");

	const { submitting, result, errorResult, execute, clearResults } =
		useBulkOrderOperation();

	const parsed = useMemo(() => parseOrderIds(rawInput), [rawInput]);
	const selectedOp =
		AMK_ORDERS_OPERATIONS.find((o) => o.value === operation) ?? null;
	const tooManyIds = parsed.valid.length > MAX_ORDER_IDS;
	const isAddBalance = operation === "AddBalance";
	const canSubmit =
		!!selectedOp &&
		!!categoryConfig &&
		parsed.valid.length > 0 &&
		!tooManyIds &&
		!submitting;

	const opLabel = (op: OperationConfig) =>
		copy?.[op.labelKey] ?? op.fallbackLabel;
	const opDescription = (op: OperationConfig) =>
		copy?.[op.descriptionKey] ?? op.fallbackDescription;

	const closeConfirm = () => {
		setConfirmOpen(false);
		setConfirmWord("");
	};

	// Changing the operation or the id list makes the previous run's result stale —
	// leaving it on screen invites reading someone else's "Already paid" list as
	// belonging to the batch now in the textarea.
	const selectOperation = (value: BulkOrderOperationType) => {
		setOperation(value);
		clearResults();
	};

	const changeInput = (value: string) => {
		setRawInput(value);
		clearResults();
	};

	const handleConfirm = async () => {
		if (!operation || !selectedOp || !categoryConfig) return;

		await execute({
			operation,
			orderIds: parsed.valid,
			driverId: categoryConfig.amkDriverId,
			...(selectedOp.supportsComment && comment.trim()
				? { comment: comment.trim() }
				: {}),
		});

		closeConfirm();
		// The textarea is deliberately NOT cleared: the operator's next step is
		// typically re-running the same batch under the next operation
		// (SetInTransport -> Deliver -> AddBalance).
	};

	const driverLabel = categoryConfig
		? `${categoryConfig.amkDriverName} (${categoryConfig.amkDriverId})`
		: "—";

	return (
		<div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={categoryStrings?.label ?? "AMK Orders"}
				subtitle={
					categoryStrings?.description ??
					"Run a bulk operation on international order batches"
				}
				backTo="/bulk-order-operations"
				backLabel={common?.back ?? "All categories"}
			/>

			{!categoryConfig && (
				<Alert
					type="error"
					showIcon
					message={
						common?.categoryUnavailable ??
						"This category is not configured for this country."
					}
				/>
			)}

			<div>
				<div className="font-medium mb-2">
					{common?.operationLabel ?? "Operation"}
				</div>
				<OperationCards
					operations={AMK_ORDERS_OPERATIONS}
					selected={operation}
					onSelect={selectOperation}
					label={opLabel}
					description={opDescription}
					dangerTagText={common?.movesMoney ?? "Moves money"}
				/>
			</div>

			{/*
				Nothing below here means anything until an operation is chosen: the driver
				attribution, the id list and the button are all per-operation.
			*/}
			{!selectedOp ? (
				<Alert
					type="info"
					showIcon
					message={
						common?.selectOperationPrompt ??
						"Select an operation to continue"
					}
				/>
			) : (
				<>
					{/*
						Restates the choice next to the inputs — the cards scroll out of view,
						and every operation here writes to production orders.
					*/}
					<div
						className={`rounded-lg border p-3 flex flex-col gap-1 ${
							selectedOp.danger
								? "border-red-500 bg-red-50"
								: "border-[#4F16C2] bg-[#4F16C2]/5"
						}`}
					>
						<span className="text-xs uppercase tracking-wide text-gray-500">
							{common?.selectedOperation ?? "Selected operation"}
						</span>
						<span className="font-medium flex items-center gap-2">
							<span
								className={
									selectedOp.danger
										? "text-red-500"
										: "text-[#4F16C2]"
								}
							>
								{selectedOp.icon}
							</span>
							{opLabel(selectedOp)}
							{selectedOp.danger && (
								<Tag color="red">
									{common?.movesMoney ?? "Moves money"}
								</Tag>
							)}
						</span>
						<span className="text-xs text-gray-500">
							{opDescription(selectedOp)}
						</span>
					</div>

					<Alert
						type="info"
						showIcon
						message={`${
							copy?.driverLabel ?? "Driver"
						}: ${driverLabel}`}
						description={
							copy?.driverStaticNote ??
							"Operations are recorded under this driver"
						}
					/>

					<OrderIdsInput
						value={rawInput}
						onChange={changeInput}
						parsed={parsed}
						tooManyIds={tooManyIds}
					/>

					{selectedOp.supportsComment && (
						<div>
							<div className="font-medium mb-2">
								{common?.commentLabel ?? "Comment (optional)"}
							</div>
							<Input
								showCount
								maxLength={MAX_COMMENT_LENGTH}
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								placeholder={
									common?.commentPlaceholder ??
									"Overrides the default status comment"
								}
							/>
						</div>
					)}

					<div>
						<Button
							size="large"
							danger={isAddBalance}
							style={
								isAddBalance ? undefined : PRIMARY_BUTTON_STYLE
							}
							type={isAddBalance ? "primary" : "default"}
							disabled={!canSubmit}
							onClick={() => setConfirmOpen(true)}
						>
							{common?.submit ?? "Execute operation"}
						</Button>
					</div>
				</>
			)}

			<ConfirmModal
				open={confirmOpen}
				operationLabel={selectedOp ? opLabel(selectedOp) : ""}
				orderCount={parsed.valid.length}
				driverLabel={driverLabel}
				danger={isAddBalance}
				dangerWarning={
					copy?.addBalanceWarning ??
					"This operation MOVES MONEY: it credits the seller balance for every order in the list. It cannot be undone from this page."
				}
				confirmWord={confirmWord}
				onConfirmWordChange={setConfirmWord}
				submitting={submitting}
				onOk={handleConfirm}
				onCancel={closeConfirm}
			/>

			<ResultPanels result={result} errorResult={errorResult} />
		</div>
	);
};

export default AmkOrders;
